import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import { Button } from '@/core/components/ui/button';
import { Skeleton } from '@/core/components/ui/skeleton';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import { cn } from '@/core/lib/utils';
import {
  Image,
  Film,
  FileJson,
  FileImage,
  FileUp,
  ExternalLink,
  RotateCcw,
  Upload,
  Trash2,
  Loader2,
  AlertTriangle,
  Play,
  Pause,
  RotateCw,
} from 'lucide-react';
import {
  AssetWithVersions,
  AssetVersion,
  AssetType,
  APPLICATION_TYPE_LABELS,
  ASSET_TYPE_LABELS,
  formatFileSize,
  formatDate,
  isVideoAsset,
  isLottieAsset,
} from '../../types/asset-types';

interface AssetDetailDialogProps {
  open: boolean;
  onClose: () => void;
  assetData: AssetWithVersions | null;
  isLoading: boolean;
  isRollingBack: boolean;
  isDeleting: boolean;
  onRollback: (assetId: number, versionId: number) => Promise<boolean>;
  onDelete: (assetId: number) => Promise<boolean>;
  onUploadNewVersion: () => void;
}

function getAssetTypeIcon(assetType: AssetType, className?: string) {
  const defaultClass = className || 'h-6 w-6';
  switch (assetType) {
    case AssetType.Icon:
      return <FileImage className={cn(defaultClass, 'text-blue-500')} />;
    case AssetType.Image:
      return <Image className={cn(defaultClass, 'text-green-500')} />;
    case AssetType.Video:
      return <Film className={cn(defaultClass, 'text-purple-500')} />;
    case AssetType.Lottie:
      return <FileJson className={cn(defaultClass, 'text-orange-500')} />;
    case AssetType.File:
      return <FileUp className={cn(defaultClass, 'text-gray-500')} />;
  }
}

// Lottie animation player component
function LottiePlayer({ url }: { url: string }) {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [animationData, setAnimationData] = useState<object | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setAnimationData(null);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load animation');
        return res.json();
      })
      .then((data) => {
        setAnimationData(data);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load animation');
        setIsLoading(false);
      });
  }, [url]);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      lottieRef.current?.pause();
    } else {
      lottieRef.current?.play();
    }
    setIsPlaying((prev) => !prev);
  }, [isPlaying]);

  const handleRestart = useCallback(() => {
    lottieRef.current?.goToAndPlay(0);
    setIsPlaying(true);
  }, []);

  const handleOpenJson = useCallback(() => {
    window.open(url, '_blank');
  }, [url]);

  if (isLoading) {
    return (
      <div className="w-full bg-gray-100 rounded-lg p-4 flex flex-col items-center justify-center min-h-[250px]">
        <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
        <p className="mt-2 text-sm text-gray-500">Loading animation...</p>
      </div>
    );
  }

  if (error || !animationData) {
    return (
      <div className="w-full bg-gray-100 rounded-lg p-4 flex flex-col items-center justify-center min-h-[250px]">
        <AlertTriangle className="h-12 w-12 text-yellow-500 mb-2" />
        <p className="text-sm text-gray-600 mb-3">{error || 'Failed to load animation'}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpenJson}
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Open JSON in New Tab
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-100 rounded-lg overflow-hidden">
      <div className="p-4 flex items-center justify-center min-h-[250px] max-h-[250px] overflow-hidden">
        <Lottie
          lottieRef={lottieRef}
          animationData={animationData}
          loop={true}
          autoplay={true}
          style={{ height: '220px', width: '100%', maxWidth: '300px' }}
        />
      </div>
      <div className="flex items-center justify-center gap-2 p-3 border-t border-gray-200 bg-gray-50">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePlayPause}
          className="h-8 px-3"
        >
          {isPlaying ? (
            <>
              <Pause className="h-4 w-4 mr-1" />
              Pause
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-1" />
              Play
            </>
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRestart}
          className="h-8 px-3"
        >
          <RotateCw className="h-4 w-4 mr-1" />
          Restart
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpenJson}
          className="h-8 px-3"
        >
          <ExternalLink className="h-4 w-4 mr-1" />
          Open JSON
        </Button>
      </div>
    </div>
  );
}

// Asset preview component
function AssetPreview({ assetData }: { assetData: AssetWithVersions }) {
  const { asset } = assetData;

  if (asset.assetType === AssetType.Icon || asset.assetType === AssetType.Image) {
    return (
      <div className="w-full bg-gray-100 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
        <img
          src={asset.currentS3Url}
          alt={asset.name}
          className="max-w-full max-h-[300px] object-contain"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.parentElement!.innerHTML =
              '<div class="text-gray-400 text-center"><p>Failed to load image</p></div>';
          }}
        />
      </div>
    );
  }

  if (asset.assetType === AssetType.Video) {
    return (
      <div className="w-full bg-black rounded-lg">
        <video
          src={asset.currentS3Url}
          controls
          className="w-full max-h-[300px]"
        >
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  if (asset.assetType === AssetType.Lottie) {
    return <LottiePlayer url={asset.currentS3Url} />;
  }

  if (asset.assetType === AssetType.File) {
    return (
      <div className="w-full bg-gray-100 rounded-lg p-6 flex flex-col items-center justify-center min-h-[200px] gap-3">
        <FileUp className="h-12 w-12 text-gray-400" />
        <p className="text-sm text-gray-500">No preview available for this file type</p>
        <a
          href={asset.currentS3Url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
        >
          <ExternalLink className="h-3 w-3" />
          Open file
        </a>
      </div>
    );
  }

  return null;
}

export function AssetDetailDialog({
  open,
  onClose,
  assetData,
  isLoading,
  isRollingBack,
  isDeleting,
  onRollback,
  onDelete,
  onUploadNewVersion,
}: AssetDetailDialogProps) {
  const [rollbackVersionId, setRollbackVersionId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleRollback = useCallback(async () => {
    if (!assetData || !rollbackVersionId) return;

    const success = await onRollback(assetData.asset.assetId, rollbackVersionId);
    if (success) {
      setRollbackVersionId(null);
    }
  }, [assetData, rollbackVersionId, onRollback]);

  const handleDelete = useCallback(async () => {
    if (!assetData) return;

    const success = await onDelete(assetData.asset.assetId);
    if (success) {
      setShowDeleteConfirm(false);
      onClose();
    }
  }, [assetData, onDelete, onClose]);

  const handleClose = useCallback(() => {
    if (!isRollingBack && !isDeleting) {
      onClose();
    }
  }, [isRollingBack, isDeleting, onClose]);

  // Sort versions by version number descending
  const sortedVersions = useMemo(() => {
    if (!assetData?.versions) return [];
    return [...assetData.versions].sort((a, b) => b.versionNumber - a.versionNumber);
  }, [assetData?.versions]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {assetData && getAssetTypeIcon(assetData.asset.assetType)}
              <span>{assetData?.asset.name || 'Asset Details'}</span>
            </DialogTitle>
            <DialogDescription>
              {assetData
                ? `${ASSET_TYPE_LABELS[assetData.asset.assetType]} for ${APPLICATION_TYPE_LABELS[assetData.asset.applicationType]}`
                : 'Loading asset details...'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6 py-4">
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-[200px] w-full rounded-lg" />
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
                <Skeleton className="h-[150px] w-full" />
              </div>
            ) : assetData ? (
              <>
                {/* Preview Section */}
                <AssetPreview assetData={assetData} />

                {/* Asset Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <p className="font-medium">{assetData.asset.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Current Version:</span>
                    <p className="font-medium">v{assetData.asset.currentVersion}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">File Size:</span>
                    <p className="font-medium">{formatFileSize(assetData.asset.fileSizeBytes)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Content Type:</span>
                    <p className="font-medium">{assetData.asset.contentType}</p>
                  </div>
                  {assetData.asset.machineId != null && (
                    <div>
                      <span className="text-gray-500">Machine ID:</span>
                      <p className="font-medium">{assetData.asset.machineId}</p>
                    </div>
                  )}
                  {assetData.asset.description && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Description:</span>
                      <p className="font-medium">{assetData.asset.description}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Created:</span>
                    <p className="font-medium">
                      {formatDate(assetData.asset.created)}
                      {assetData.asset.createdByName && ` by ${assetData.asset.createdByName}`}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Last Modified:</span>
                    <p className="font-medium">
                      {formatDate(assetData.asset.modified)}
                      {assetData.asset.modifiedByName && ` by ${assetData.asset.modifiedByName}`}
                    </p>
                  </div>
                </div>

                {/* Version History */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Version History</h4>
                  <div className="rounded-md border border-[#E8E8E8] overflow-hidden">
                    <table className="w-full">
                      <thead className={GRID_STYLES.header.background}>
                        <tr>
                          <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-20')}>Version</th>
                          <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Filename</th>
                          <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Size</th>
                          <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-36')}>Date</th>
                          <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-28')}>Uploaded By</th>
                          <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedVersions.map((version) => {
                          const isCurrent = version.versionNumber === assetData.asset.currentVersion;
                          return (
                            <tr
                              key={version.versionId}
                              className={cn(
                                GRID_STYLES.row.default,
                                GRID_STYLES.row.hover,
                                `border-b ${GRID_STYLES.border}`,
                                isCurrent && 'bg-blue-50'
                              )}
                            >
                              <td className={cn(GRID_STYLES.cell.compact, 'text-sm')}>
                                <span className={cn(
                                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                  isCurrent ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                )}>
                                  v{version.versionNumber}
                                  {isCurrent && ' (current)'}
                                </span>
                              </td>
                              <td className={cn(GRID_STYLES.cell.compact, 'text-sm truncate')} title={version.originalFileName}>
                                {version.originalFileName}
                              </td>
                              <td className={cn(GRID_STYLES.cell.compact, 'text-sm text-gray-600')}>
                                {formatFileSize(version.fileSizeBytes)}
                              </td>
                              <td className={cn(GRID_STYLES.cell.compact, 'text-sm text-gray-600')}>
                                {formatDate(version.created)}
                              </td>
                              <td className={cn(GRID_STYLES.cell.compact, 'text-sm text-gray-600 truncate')} title={version.createdByName || undefined}>
                                {version.createdByName || '-'}
                              </td>
                              <td className={GRID_STYLES.cell.compact}>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => window.open(version.s3Url, '_blank')}
                                    className="h-7 w-7 p-0"
                                    title="Preview"
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                  </Button>
                                  {!isCurrent && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setRollbackVersionId(version.versionId)}
                                      className="h-7 w-7 p-0 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                      title="Rollback to this version"
                                      disabled={isRollingBack}
                                    >
                                      <RotateCcw className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
                <p className="mt-2 text-gray-600">Failed to load asset details</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 gap-2">
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading || isRollingBack || isDeleting}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={handleClose} disabled={isRollingBack || isDeleting}>
              Close
            </Button>
            <Button onClick={onUploadNewVersion} disabled={isLoading || isRollingBack || isDeleting}>
              <Upload className="h-4 w-4 mr-2" />
              Upload New Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback Confirmation */}
      <AlertDialog open={rollbackVersionId !== null} onOpenChange={() => setRollbackVersionId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rollback to Previous Version?</AlertDialogTitle>
            <AlertDialogDescription>
              This will set the selected version as the current version. The current version will remain in the history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRollingBack}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRollback} disabled={isRollingBack}>
              {isRollingBack ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Rolling back...
                </>
              ) : (
                'Rollback'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{assetData?.asset.name}&quot; and all its versions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
